import _ from "lodash";
import React, { Suspense } from "react";
import PropTypes from "prop-types";
import classes from "./Drive.scss";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getFileExtensionFromMimeType } from "Utils";
import {
  getFileExtensionFromFilename,
  getMimeTypeFromFileName,
} from "Utils/AttachmentHelpers";
import { unsupportedFileExtensionError } from "modules/Services";
import ACLStore from "lib/aclStore";
import { BLOCKED_FILE_EXTENSIONS, BLOCKED_FILE_MIME_TYPES } from "Constants";
import Loading from "UIComponents/Loading";

const GraphFileBrowser = React.lazy(async () => {
  const { GraphFileBrowser } = await import(
    /* webpackChunkName: "microsoft-file-browser" */ "@microsoft/file-browser"
  );
  return { default: GraphFileBrowser };
});

import axios from "axios";

import { connect } from "react-redux";
import UIModal from "UIComponents/UIModal";

class OneDrivePicker extends React.PureComponent {
  constructor(props) {
    super(props);
    // if (props.updateRef) {
    //   props.updateRef(this);
    // }
  }

  componentDidMount = () => {
    this.mounted = true;
  };

  componentWillUnmount = () => {
    this.mounted = false;
  };

  onNotAllowedFileType = ({ notAllowedFileCount, fileFormats }) => {
    const { setToastMsg } = this.props;
    const fileFormatCount = _.size(fileFormats);

    setToastMsg({
      msg:
        "toastMsgs:fileCount_file_will_be_not_uploaded_only_fileFormate_format_allowed",
      locale_params: [
        {
          key: "fileCount",
          value: notAllowedFileCount,
          isPlainText: true,
        },
        {
          key: "fileFormatCount",
          value: fileFormatCount,
          isPlainText: true,
        },
        {
          key: "entity1",
          value: `"${_.join(_.slice(fileFormats, 0, -1), ", ")}"`,
          isPlainText: true,
        },
        {
          key: "entity2",
          value: `"${_.last(fileFormats)}"`,
          isPlainText: true,
        },
      ],
      type: "warning",
      timeOut: 3500,
      position: "toast-top-center",
    });
  };

  onCancel = () => {
    if (!this.mounted) {
      return;
    }
    if (this.props.onClose) {
      this.props.onClose();
    }
  };

  getAuthenticationToken = () => {
    return new Promise(resolve => resolve(this.props.accessToken));
  };

  getFilesInfo = async ({ files: selectedFiles }) => {
    const {
      accept,
      accessToken,
      onChoose,
      excludeFileExtensionsList,
      unsupportedFileExtensionError,
      customFilterFilesHandler,
    } = this.props;
    const filesInfo = [];

    _.forEach(selectedFiles, selectedFile => {
      filesInfo.push(
        axios.get(`${selectedFile[0]}/drive/items/${selectedFile[2]}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        })
      );
    });
    //fetching file info of selected files
    const files = _.map(
      _.filter(await Promise.all(filesInfo), responseObj => responseObj?.data),
      fileResponse => {
        const fileInfo = fileResponse.data;
        return {
          downloadUrl: fileInfo["@microsoft.graph.downloadUrl"],
          url: _.get(fileInfo, "webUrl"),
          mimeType: _.get(fileInfo, "file.mimeType", ""),
          size: _.get(fileInfo, "size"),
          name: _.get(fileInfo, "name"),
          id: _.get(fileInfo, "id"),
        };
      }
    );

    let allowedFiles = files;
    //checking if files are in allowed file formats
    // Note: extension is taken from the mimeType
    if (!_.isEmpty(accept)) {
      allowedFiles = _.filter(files, file => _.includes(accept, file.mimeType));
    }

    const notAllowedFileTypeCount = _.size(files) - _.size(allowedFiles);
    if (notAllowedFileTypeCount > 0) {
      this.onNotAllowedFileType({
        notAllowedFileCount: notAllowedFileTypeCount,
        fileFormats: _.filter(
          _.map(accept, allowedType => {
            let fileExtension = getFileExtensionFromMimeType({
              mimeType: allowedType,
            });
            // as mov and qt both have same mimeType video/quicktime, we will have to explicitly handle restriction of qt files
            if (
              fileExtension === "qt" &&
              _.includes(excludeFileExtensionsList, ".qt")
            ) {
              fileExtension = "mov";
            }

            if (fileExtension) return `.${fileExtension}`;
          }),
          val => !_.isEmpty(val)
        ),
      });
    }

    // filtering files on basis of excludedFileExtensionList
    // Note: extension is taken from the fileName
    const blockMaliciousMimeTypes = ACLStore.can(
      "FeatureFlag:BlockMaliciousMimeType"
    );
    const blockedFileExtensions = blockMaliciousMimeTypes
      ? _.union(excludeFileExtensionsList, BLOCKED_FILE_EXTENSIONS)
      : excludeFileExtensionsList;
    const blockedFileMimeTypes = blockMaliciousMimeTypes
      ? BLOCKED_FILE_MIME_TYPES
      : [];
    const notAllowedFileExtensions = [];
    allowedFiles = _.filter(allowedFiles, file => {
      const mimeType =
        file?.mimeType || getMimeTypeFromFileName({ fileName: file?.name });

      const extension = `.${
        getFileExtensionFromFilename({
          fileName: file?.name,
        }) || getFileExtensionFromMimeType({ mimeType })
      }`;

      if (
        _.includes(blockedFileExtensions, extension) ||
        _.includes(blockedFileMimeTypes, mimeType)
      ) {
        notAllowedFileExtensions.push(extension);
        return false;
      }
      return true;
    });

    if (_.size(notAllowedFileExtensions) > 0) {
      unsupportedFileExtensionError({
        notAllowedFileExtensions: _.uniq(notAllowedFileExtensions),
        notAllowedFileCount: _.size(notAllowedFileExtensions),
      });
    }

    // filter files based on custom logic
    if (customFilterFilesHandler) {
      allowedFiles = customFilterFilesHandler(allowedFiles);
    }

    if (_.isEmpty(allowedFiles)) {
      this.onCancel();
      return;
    }

    onChoose(allowedFiles);
  };

  onSuccess = keys => {
    const { multiselect } = this.props;

    if (multiselect) {
      const files = _.reduce(
        keys,
        (result, item) => {
          const file = item["driveItem_203"];
          if (file) {
            return [...result, file];
          }
          return result;
        },
        []
      );

      this.getFilesInfo({ files });
    } else {
      const item = keys[0];
      const file = item["driveItem_203"];
      if (file) {
        this.getFilesInfo({
          files: [file],
        });
      }
    }
    this.onCancel();
  };

  render() {
    const { multiselect } = this.props;
    return (
      <UIModal
        isOpen={true}
        modalContent={classes.modalContent}
        onRequestClose={this.onCancel}
        customStyle={{ overlay: { zIndex: 1151 } }}
      >
        <Suspense fallback={<Loading containerStyle={{ height: "100%" }} />}>
          <div
            onClick={e => {
              e.stopPropagation();
            }}
          >
            <GraphFileBrowser
              getAuthenticationToken={this.getAuthenticationToken}
              onSuccess={this.onSuccess}
              onCancel={this.onCancel}
              selectionMode={multiselect ? "multiple" : "single"}
            />
          </div>
        </Suspense>
      </UIModal>
    );
  }
}

OneDrivePicker.propTypes = {
  multiselect: PropTypes.bool,
  onChoose: PropTypes.func,
};

OneDrivePicker.defaultProps = {
  onChoose: () => {},
};

const mapActionCreators = {
  setToastMsg,
  unsupportedFileExtensionError,
};

export default connect(null, mapActionCreators)(OneDrivePicker);
